import React, {useEffect, useState} from 'react';
import {Modal} from '../../core/layout/modal';
import {Helmet} from 'react-helmet';
import {ApplicationShell} from 'src/core/layout/application-shell';
import {ProjectCard} from './project-card/project-card';
import {RenameProjectModal} from 'src/core/components/projects-rename-modal/rename-modal';
import {useAxios} from 'src/utils/http';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {
  createProject,
  deleteProject,
  DeleteProjectRequest,
  ProjectCreateRequest,
  ProjectCreateRequests,
  updateProjects,
} from 'src/models/project';
import {useAuth} from 'src/hooks/auth';
import {PlusIcon} from '@heroicons/react/solid';
import {trackEvent} from 'src/utils/tracking';
import {HiOutlineArrowRight} from 'react-icons/hi';
import {getDatasetsByProject} from 'src/models/dataset';
import {isInternal} from 'src/models/auth';
import {toast} from 'react-toastify';
import {ToastMessage} from 'src/core/components/toast';
import {AxiosError} from 'axios';
import {Loading} from 'src/core/components/loading';

export const ProjectListPage = () => {
  const http = useAxios();
  const {authState} = useAuth();
  const queryClient = useQueryClient();
  const isInternalUser = isInternal(authState.profile?.email || '');
  const [projects, setProjects] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [expandedProjectName, setExpandedProjectName] = useState<string | null>(
    null
  );

  const {data: projectDataByOwner, isLoading: isProjectsLoading} = useQuery(
    ['projectsByOwner', authState.profile?.userId],
    () => getDatasetsByProject(http),
    {
      keepPreviousData: true,
      // Avoid refetch storms that trip the IP rate limiter (general_limit is
      // 1000 req / 10s per IP — easy to hit with hot reload + multiple tabs).
      // Mutations below invalidate this query explicitly, so a short stale
      // window is enough.
      staleTime: 30 * 1000,
    }
  );

  const invalidateProjects = async () => {
    await queryClient.invalidateQueries([
      'projectsByOwner',
      authState.profile?.userId,
    ]);
  };

  const makeProjectKey = (
    name: string,
    type: 'Open' | 'Closed' | 'Shared' | 'Ghosted'
  ) => `${type} - ${name}`;

  const myProjects =
    projectDataByOwner?.filter(
      p => p.project_owner_id === authState.profile?.userId
    ) || [];

  const myOpenProjects = myProjects
    .filter(p => p.project_state === 'open')
    .map(p => ({
      ...p,
      datasets: p.datasets.filter(d => d.userId === authState.profile?.userId),
    }))
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, {sensitivity: 'base'})
    );

  const myClosedProjects = myProjects
    .filter(p => p.project_state === 'closed')
    .map(p => ({
      ...p,
      datasets: p.datasets.filter(d => d.userId === authState.profile?.userId),
    }))
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, {sensitivity: 'base'})
    );

  // Ghosted = I own project, datasets transferred to others. Hidden for MVP.
  const ghostedProjects = myProjects
    .map(p => ({
      ...p,
      datasets: p.datasets.filter(d => d.userId !== authState.profile?.userId),
    }))
    .filter(p => p.datasets.length > 0)
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, {sensitivity: 'base'})
    );

  // Shared = Someone else's project, datasets transferred to me. Hidden for MVP.
  const sharedProjects =
    projectDataByOwner
      ?.filter(p => p.project_owner_id !== authState.profile?.userId)
      .map(p => ({
        ...p,
        datasets: p.datasets.filter(
          d => d.userId === authState.profile?.userId
        ),
      }))
      .filter(p => p.datasets.length > 0)
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, {sensitivity: 'base'})
      ) || [];

  /** Set to true to show "Shared with me" and "Shared with others" sections. */
  const SHOW_SHARING_UI = false;

  useEffect(() => {
    if (projectDataByOwner) {
      const updatedProjects = projectDataByOwner.map(
        (p: {project_state: string}) => ({
          ...p,
          isLocked: p.project_state === 'closed',
        })
      );
      setProjects(updatedProjects);
    }
  }, [projectDataByOwner]);

  const createProjectMutation = useMutation({
    mutationFn: (request: ProjectCreateRequest) => createProject(http, request),
    onSuccess: async data => {
      trackEvent('CLICK_CREATE_PROJECT', {
        project_id: data.project_id,
      });
      await invalidateProjects();
      toast(<ToastMessage title="New project is created" icon="success" />);
      setNewProjectName('');
      setIsModalOpen(false);
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{message?: string}>;
      const status = err.response?.status;
      let msg =
        err.response?.data?.message ?? 'Something went wrong, try again later';
      if (status === 429) {
        msg = 'Too many requests — please wait a moment and try again.';
      } else if (status === 409) {
        msg =
          err.response?.data?.message ??
          'A project with that name already exists.';
      }
      toast.error(msg);
    },
  });

  const updateProjectMutation = useMutation({
    mutationFn: (request: ProjectCreateRequests) =>
      updateProjects(http, request),
    onSuccess: async data => {
      trackEvent('CLICK_UPDATE_PROJECT', {
        project_id: data.project_id,
      });
      await invalidateProjects();
    },
    onError: error => {
      console.error('Project update failed:', error);
    },
  });

  const deleteProjectMutation = useMutation({
    mutationFn: (request: DeleteProjectRequest) => deleteProject(http, request),
    onSuccess: async data => {
      trackEvent('CLICK_DELETE_PROJECT', {
        project_id: data.project_id,
      });
      await invalidateProjects();
      toast(<ToastMessage title="Project deleted" icon="success" />);
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{message?: string}>;
      const msg =
        err.response?.data?.message ?? 'Something went wrong, try again later';
      toast.error(msg);
    },
  });

  const handleDeleteProject = (projectId: string) => {
    const projectToDelete = projects.find(
      (p: any) => p.project_id === projectId
    );
    if (projectToDelete) {
      const type = projectToDelete.project_state === 'open' ? 'Open' : 'Closed';
      const projectKey = makeProjectKey(projectToDelete.name, type);

      if (expandedProjectName === projectKey) {
        setExpandedProjectName(null);
      }
      deleteProjectMutation.mutate({
        project_id: projectToDelete.project_id,
        user_id: authState.profile?.userId ?? 0,
      });
    } else {
      console.error('Project not found with ID:', projectId);
    }
  };

  const handleCreateProject = () => {
    if (!newProjectName.trim()) return;

    createProjectMutation.mutate({
      name: newProjectName.trim(),
      project_state: 'open',
    });
  };

  const handleCloseProject = (id: string) => {
    const projectToClose = projects.find((p: any) => p.project_id === id);
    if (projectToClose) {
      const newState =
        projectToClose.project_state === 'open' ? 'closed' : 'open';
      const type = projectToClose.project_state === 'open' ? 'Open' : 'Closed';
      const projectKey = makeProjectKey(projectToClose.name, type);

      if (expandedProjectName === projectKey) {
        setExpandedProjectName(null);
      }
      updateProjectMutation.mutate({
        project_id: projectToClose.project_id,
        name: projectToClose.name,
        project_state: newState,
      });
    }
  };

  const handleRenameProject = (newName: string) => {
    if (currentProjectId !== null) {
      const projectToRename = projects.find(
        (p: any) => p.project_id === currentProjectId
      );
      if (projectToRename) {
        updateProjectMutation.mutate({
          project_id: projectToRename.project_id,
          name: newName,
          project_state: projectToRename.project_state,
        });
      }
    }
  };

  const toggleProjectLock = (project_id: string) => {
    const projectToLock = projects.find(p => p.project_id === project_id);
    if (projectToLock) {
      setProjects(prev =>
        prev.map(p =>
          p.project_id === project_id
            ? {
                ...p,
                isLocked: !p.isLocked,
                project_state: p.project_state === 'open' ? 'closed' : 'open',
              }
            : p
        )
      );

      handleCloseProject(project_id);
    }
  };

  const openRenameModal = (id: string) => {
    setCurrentProjectId(id);
    setIsRenameModalOpen(true);
  };

  return (
    <>
      <Helmet>
        <title>Segmed Openda - Order Container Details</title>
      </Helmet>
      <ApplicationShell
        bgcolor="bgcolor"
        contained={false}
        navbar={
          <div className="navbar">
            <div className="ml-auto text-sm text-gray-700">
              Having trouble finding data?
              <a
                className="link ml-1"
                href="https://calendly.com/d/dr3-5n8-mjd/segmed-demo-request"
                target="_blank"
                rel="noreferrer"
              >
                Schedule a help session
                <HiOutlineArrowRight className="inline-block ml-1" />
              </a>
            </div>
          </div>
        }
      >
        <div className="p-6">
          {isProjectsLoading ? (
            <Loading text="Loading projects..." />
          ) : (
            <>
              <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
                <h2 className="text-xl font-semibold text-gray-800 mb-2">
                  Projects
                </h2>

                <p className="text-gray-600 text-sm mt-2 w-full mb-1">
                  Projects are a top level container designed to help you better
                  organize your work by grouping Datasets. Datasets can only be
                  created within the context of a Project.
                </p>
                <p className="text-gray-600 text-sm mt-2 w-full mb-1">
                  Any previous Datasets have been migrated to the Default
                  Project. You are encouraged to create new Projects and use the
                  Move function to organize your existing datasets.
                </p>

                <div className="flex items-center gap-8">
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="bg-blue-600 items-center text-white px-8 py-1.5 rounded hover:bg-blue-700"
                  >
                    <PlusIcon className="w-4 h-4 inline" />
                    <span className="ml-2 text-sm">New Project</span>
                  </button>
                </div>
              </div>

              {/* Handle case when there are no projects */}
              <>
                {/* My Projects */}
                <div className="mt-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-6">
                    My Projects
                  </h2>
                  {myOpenProjects.length === 0 ? (
                    <p className="text-gray-400 text-sm">No open projects</p>
                  ) : (
                    myOpenProjects.map(project => {
                      const key = makeProjectKey(project.name, 'Open');
                      return (
                        <ProjectCard
                          key={project.project_id}
                          projectId={project.project_id}
                          projectName={project.name}
                          isLocked={false}
                          isShared={false}
                          defaultExpanded={expandedProjectName === key}
                          onExpand={name =>
                            setExpandedProjectName(name ? key : null)
                          }
                          onClose={() =>
                            handleDeleteProject(project.project_id)
                          }
                          onToggleLock={() =>
                            toggleProjectLock(project.project_id)
                          }
                          onRename={() => openRenameModal(project.project_id)}
                          datasets={project.datasets}
                          isInternalUser={isInternalUser}
                          datasetsLoading={isProjectsLoading}
                          allProjects={myOpenProjects}
                          onDatasetMoved={invalidateProjects}
                        />
                      );
                    })
                  )}
                </div>
              </>

              {SHOW_SHARING_UI && (
                <section className="mt-10">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">
                    Projects (Shared with me)
                  </h2>
                  {sharedProjects.length === 0 ? (
                    <p className="text-gray-400 text-sm">No shared projects</p>
                  ) : (
                    sharedProjects.map(project => {
                      const key = makeProjectKey(project.name, 'Shared');
                      return (
                        <ProjectCard
                          key={project.project_id}
                          projectId={project.project_id}
                          projectName={project.name}
                          isShared={true}
                          defaultExpanded={expandedProjectName === key}
                          onExpand={name =>
                            setExpandedProjectName(name ? key : null)
                          }
                          onClose={() => {
                            /* you probably don’t allow deleting others’ projects */
                          }}
                          onToggleLock={() => {}}
                          onRename={() => {}}
                          datasets={project.datasets}
                          isInternalUser={isInternalUser}
                        />
                      );
                    })
                  )}
                </section>
              )}

              {SHOW_SHARING_UI && (
                <section className="mt-10">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">
                    Projects (Shared With others)
                  </h2>
                  {ghostedProjects.length === 0 ? (
                    <p className="text-gray-400 text-sm">
                      No projects shared with others.
                    </p>
                  ) : (
                    ghostedProjects.map(project => {
                      const key = makeProjectKey(project.name, 'Ghosted');
                      return (
                        <ProjectCard
                          key={project.project_id}
                          projectId={project.project_id}
                          projectName={project.name}
                          isShared={true}
                          isGhosted={true}
                          defaultExpanded={expandedProjectName === key}
                          onExpand={name =>
                            setExpandedProjectName(name ? key : null)
                          }
                          onClose={() => {}}
                          onToggleLock={() => {}}
                          onRename={() => {}}
                          datasets={project.datasets}
                          isInternalUser={isInternalUser}
                        />
                      );
                    })
                  )}
                </section>
              )}

              {/* Closed Projects */}
              <section className="mt-10">
                <h2 className="text-xl font-semibold text-gray-800 mb-6">
                  Closed Projects
                </h2>
                {myClosedProjects.length === 0 ? (
                  <p className="text-gray-400 text-sm">No closed projects</p>
                ) : (
                  myClosedProjects.map(project => {
                    const key = makeProjectKey(project.name, 'Closed');
                    return (
                      <ProjectCard
                        key={project.project_id}
                        projectId={project.project_id}
                        projectName={project.name}
                        isLocked={true}
                        isShared={false}
                        defaultExpanded={expandedProjectName === key}
                        onExpand={name =>
                          setExpandedProjectName(name ? key : null)
                        }
                        onClose={() => handleDeleteProject(project.project_id)}
                        onToggleLock={() =>
                          toggleProjectLock(project.project_id)
                        }
                        onRename={() => openRenameModal(project.project_id)}
                        datasets={project.datasets}
                        isInternalUser={isInternalUser}
                        datasetsLoading={isProjectsLoading}
                        allProjects={myOpenProjects}
                        onDatasetMoved={invalidateProjects}
                      />
                    );
                  })
                )}
              </section>
            </>
          )}

          <RenameProjectModal
            isOpen={isRenameModalOpen}
            onClose={() => setIsRenameModalOpen(false)}
            onRename={handleRenameProject}
            projectName={
              projects.find((p: any) => p.project_id === currentProjectId)
                ?.name ?? ''
            }
          />

          <Modal
            isOpen={isModalOpen}
            onRequestClose={() => {
              setNewProjectName('');
              setIsModalOpen(false);
            }}
            className="w-full max-w-md"
          >
            <div>
              <h2 className="text-xl font-semibold mb-4">Create New Project</h2>
              <input
                type="text"
                placeholder="Enter project name"
                value={newProjectName}
                onChange={e => setNewProjectName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreateProject();
                  }
                }}
                autoFocus
                disabled={createProjectMutation.isLoading}
                className="border border-gray-300 w-full p-2 mb-4 rounded disabled:bg-gray-100"
              />
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setNewProjectName('');
                    setIsModalOpen(false);
                  }}
                  disabled={createProjectMutation.isLoading}
                  className="bg-white text-gray-700 border border-gray-300 px-3 py-1 rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateProject}
                  disabled={
                    !newProjectName.trim() || createProjectMutation.isLoading
                  }
                  className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
                >
                  {createProjectMutation.isLoading
                    ? 'Creating…'
                    : 'Create Project'}
                </button>
              </div>
            </div>
          </Modal>
        </div>
      </ApplicationShell>
    </>
  );
};
